import type {
  InterventionGroupDto,
  InterventionGroupTemplateResponse,
  PlacementRule,
  TreatmentPlanDto,
  TreatmentPlanTemplateResponse,
  TreatmentWeekDto
} from '../api/generated';

// Rewrites a cloned group's placement rules onto the fresh intervention ids the
// clone minted. A rule that kept naming a template id owns no intervention in
// the new group, and the backend attaches each rule to the session of the
// intervention that owns it - so it would silently never reach the solver. A
// reference that resolves to nothing (a rule left behind by an intervention
// since dropped from the template) is left out for the same reason, rather than
// carried along as a dangling id. Positional rules (FIRST / LAST) name only the
// intervention they pin; their absent counterpart stays absent.
function rewritePlacementRules(
  rules: PlacementRule[],
  idByTemplateInterventionId: ReadonlyMap<string, string>
): PlacementRule[] {
  const rewritten: PlacementRule[] = [];
  for (const rule of rules) {
    const source = rule.sourceInterventionId
      ? idByTemplateInterventionId.get(rule.sourceInterventionId)
      : undefined;
    const target = rule.targetInterventionId
      ? idByTemplateInterventionId.get(rule.targetInterventionId)
      : undefined;
    if ((rule.sourceInterventionId && !source) || (rule.targetInterventionId && !target)) continue;
    rewritten.push({ ...rule, sourceInterventionId: source, targetInterventionId: target });
  }
  return rewritten;
}

// Creates a new intervention group from a template group, with fresh ids
function buildGroupFromTemplate(
  templateGroup: InterventionGroupTemplateResponse,
  capacity: number | undefined
): InterventionGroupDto {
  // The interventions get fresh ids, so the group's placement rules have to be
  // rewritten to them.
  const idByTemplateInterventionId = new Map<string, string>();
  const interventions = templateGroup.interventions.map((iv) => {
    const id = crypto.randomUUID();
    idByTemplateInterventionId.set(iv.id, id);
    return {
      id,
      interventionTemplateId: iv.id,
      name: iv.name,
      description: iv.description,
      color: iv.color,
      icon: iv.icon,
      durationMinutes: iv.durationMinutes,
      sessionType: iv.sessionType,
      mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements,
      flexStaffCompetenceRequirements: iv.flexStaffCompetenceRequirements,
      allowedDays: iv.allowedDays,
      roomRequirementIds: iv.roomRequirements?.flatMap((r) => (r.id ? [r.id] : []))
    };
  });

  return {
    id: crypto.randomUUID(),
    name: templateGroup.name,
    icon: templateGroup.icon,
    order: templateGroup.order,
    barSize: templateGroup.barSize,
    reimbursementAmount: templateGroup.reimbursementAmount,
    patientType: templateGroup.patientType,
    capacity: capacity ?? 0,
    interventions,
    placementRules: rewritePlacementRules(
      templateGroup.placementRules,
      idByTemplateInterventionId
    )
  };
}

// Creates the weeks for a new plan from a template, shifted by the given week offset
export function buildWeeksFromTemplate(
  template: TreatmentPlanTemplateResponse,
  weekOffset: number,
  capacity: number | undefined
): TreatmentWeekDto[] {
  return template.weeks.map((tw) => ({
    weekNumber: tw.weekNumber + weekOffset,
    groups: tw.groups.map((tg) => buildGroupFromTemplate(tg, capacity))
  }));
}

// A new treatment plan instance of the template holding the given weeks.
export function buildTreatmentPlanFromTemplate(
  template: TreatmentPlanTemplateResponse,
  weeks: TreatmentWeekDto[]
): TreatmentPlanDto {
  return {
    id: crypto.randomUUID(),
    treatmentPlanTemplateId: template.id,
    name: template.name,
    strokeColor: template.strokeColor,
    fillColor: template.fillColor,
    highlightColor: template.highlightColor,
    // The plan carries its own snapshot of the template's completion amount —
    // the backend has no template→plan copy step, so dropping it here would
    // permanently save the plan without one.
    completionReimbursementAmount: template.completionReimbursementAmount,
    weeks
  };
}

// Template weeks are numbered from 1, but nothing guarantees it, so a roll-out
// offsets from the lowest week the template actually has.
export function getTemplateFirstWeekNumber(template: TreatmentPlanTemplateResponse): number {
  const weekNumbers = template.weeks.map((w) => w.weekNumber);
  return weekNumbers.length > 0 ? Math.min(...weekNumbers) : 0;
}
